import * as React from 'react';
import AddAiCreditsDialog from 'src/components/addAiCredits';
import AiAssistant from 'src/components/aiAssistant';
import SubscriptionCancelNotice from 'src/components/subscriptionCancelNotice';
import { useGetMyExperimentsQuery } from 'src/redux/services/experiments';
import AppMessageHandler from 'src/components/appMessageHandler';
import { useRoutes, BrowserRouter as Router } from 'react-router-dom';
import MakingBetter from 'src/components/makingBetter';
import { userSelf } from 'src/redux/actions/auth';
import { getStoreItems } from 'src/redux/actions/store';
import { useAppDispatch, useAppSelector } from 'src/redux';
import OfflineComponent from 'src/components/offlineComponent';
import {
  setIsOffline,
  setIsServerNotAvailable,
  setIsSocketConnected,
} from 'src/redux/slices/appState';
import { LS_KEY_LOGIN_TOKEN } from 'src/constants/localStorageKeys';
import rememberReturnPath from 'src/helpers/returnPath';
import { reloadForChunkFailure } from 'src/helpers/chunkLoadError';
import { setCredits, setXp } from 'src/redux/slices/auth';
import type { XpChangePayload } from 'src/redux/types/auth';
import { updateApplicantMetadata } from 'src/redux/slices/candidateLayout';
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from 'notistack';
import useAppNavigate from 'src/helpers/hooks/useAppNavigate';
import useReleaseStaleScrollLock from 'src/helpers/hooks/useReleaseStaleScrollLock';
import { usePushSubscriptionSync } from 'src/helpers/hooks/usePushSubscription';
import { isAdminMode } from 'src/helpers/storedToken';
import { dropSignedOutPushSubscription } from 'src/helpers/webPush';
import useRoutePattern from 'src/helpers/hooks/useRoutePattern';
import useAccountMemory from 'src/helpers/hooks/useAccountMemory';
import type {
  AiInviteResponse,
  AiMultiplayerInviteNotifType,
  MultiplayerRoomInviteNotifType,
} from 'src/pages/skillBuilder/types/Multiplayer';
import InviteToast from 'src/pages/skillBuilder/components/multiplay/InviteToast';
import { playChime, warmChime } from 'src/helpers/chime';
import {
  acceptAiMultiplayerInvite,
  joinMultiplayerSession,
  muteMultiplayerInviter,
  respondAiMultiplayerInvite,
} from 'src/redux/actions/skillBuilder';
import socket from 'src/socket';
import isDoNotDisturbPath from 'src/constants/doNotDisturb';
import useReportAwayState from 'src/helpers/hooks/useReportAwayState';
import getRoutes from './config';

/**
 * A person's invites on screen right now. An AI invite waits for the next one
 * rather than stacking on top: the person is the better offer.
 */
const openPersonInvites = new Set<SnackbarKey>();

/** The AI invite on screen, if any. Only ever one: a second waits its turn. */
let openAiInvite: SnackbarKey | null = null;

const Routes = () => {
  const {
    user,
    token: userToken,
    isLoginSuccess,
  } = useAppSelector((state) => state.auth);

  const routes = React.useMemo(
    () => getRoutes(isLoginSuccess),
    [isLoginSuccess],
  );

  const appRoutes = useRoutes(routes);
  // Keeps CTA events tagged with the route pattern rather than the raw pathname.
  useRoutePattern(routes);
  useReleaseStaleScrollLock();
  usePushSubscriptionSync();
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { isOffline } = useAppSelector((state) => state.appState);
  const userId = user.id;
  useReportAwayState(!!userId && !isOffline);

  React.useEffect(() => {
    const onConnect = () => {
      socket.emit('register_user', { user_id: userId });
      dispatch(setIsSocketConnected(true));
    };

    const onDisconnect = () => {
      dispatch(setIsSocketConnected(false));
    };

    // Backend pushes the post-change balance so the credit counter updates live.
    const onCreditChange = (payload: { credits: number; change: number }) => {
      if (typeof payload?.credits === 'number') {
        dispatch(setCredits(payload.credits));
      }
    };

    // Same contract for AI Credits, pushed by modify_user_ai_credits — every
    // grant and spend, not just the vault's own top-up, so the balance stays
    // right while an AI Guide session burns through it in another tab.
    const onAiCreditChange = (payload: {
      ai_credits: number;
      change: number;
      pro_ai_credits?: number;
      pro_ai_credits_expires_at?: string | null;
    }) => {
      if (typeof payload?.ai_credits === 'number') {
        dispatch(
          updateApplicantMetadata({
            remaining_ai_interactions: payload.ai_credits,
            // Absent from servers that predate Pro+ credits expiring.
            ...(typeof payload.pro_ai_credits === 'number' && {
              pro_ai_credits: payload.pro_ai_credits,
              pro_ai_credits_expires_at: payload.pro_ai_credits_expires_at,
            }),
          }),
        );
      }
    };

    // Same shape as credits: the server sends the new balance whenever XP is
    // awarded — a finished round, a topper bonus, a lesson, a topic test — so
    // the header stays right without refetching the user. The ladder fields ride
    // along so the navbar bar can move without a /user/self round trip.
    //
    // The payload is flat (one socket message, not a nested object), so it is
    // reshaped here into the same `level` shape /user/self serves — one shape in
    // the store means the bar has one thing to read.
    const onXpChange = (payload: XpChangePayload) => {
      if (typeof payload?.xp !== 'number') return;
      const hasLevel = typeof payload.level === 'number';
      dispatch(
        setXp({
          xp: payload.xp,
          change: payload.change,
          levelledUp: payload.levelled_up ?? [],
          level: hasLevel
            ? {
                xp: payload.xp,
                level: payload.level,
                level_name: payload.level_name,
                level_floor_xp: payload.level_floor_xp,
                next_level_xp: payload.next_level_xp,
                next_level_name: payload.next_level_name,
                into_band: payload.into_band,
                band_size: payload.band_size,
                is_max_level: payload.is_max_level,
              }
            : null,
        }),
      );
    };

    /**
     * A room invite has to reach the learner wherever they are — that is the
     * whole point of it, so it is registered here rather than in any
     * multiplayer screen. Joining reuses the existing ?code= handler on the
     * multiplayer home, so there is one join path however you arrive.
     */
    const onRoomInvite = (payload: MultiplayerRoomInviteNotifType) => {
      if (!payload?.room_code) return;
      // A room route is in the list too: accepting would pull them out of the
      // room they are already sitting in, so the prompt is at best noise and at
      // worst a trap. Path is the signal — no room membership lives in the
      // store, and a lobby route is only ever reached by being in that room.
      const { pathname } = window.location;
      if (isDoNotDisturbPath(pathname)) return;
      playChime();

      const inviteKey = enqueueSnackbar('', {
        persist: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        onExited: (_node, key) => openPersonInvites.delete(key),
        // Fully custom content: the stock `info` bar could not show who was
        // asking, and an invitation without a face reads as a system notice.
        content: (key: SnackbarKey) => (
          <InviteToast
            id={key}
            payload={payload}
            onDismiss={() => closeSnackbar(key)}
            onOpenSettings={() => {
              closeSnackbar(key);
              navigate('/settings');
            }}
            onMute={
              payload.from_user_id
                ? () => {
                    closeSnackbar(key);
                    dispatch(
                      muteMultiplayerInviter({
                        fromUserId: payload.from_user_id!,
                      }),
                    ).then((res) => {
                      if (!muteMultiplayerInviter.fulfilled.match(res)) return;
                      const mins = Math.round(
                        (res.payload?.muted_for_seconds ?? 300) / 60,
                      );
                      enqueueSnackbar(
                        `You won't get invites from ${payload.from_user_name || 'this player'} for ${mins} minutes.`,
                        { variant: 'success' },
                      );
                    });
                  }
                : undefined
            }
            onJoin={async () => {
              closeSnackbar(key);
              const res = await dispatch(
                joinMultiplayerSession({
                  params: { room_code: payload.room_code! },
                }),
              );
              if (
                joinMultiplayerSession.fulfilled.match(res) &&
                res.payload?.id
              )
                navigate(`/skill-builder/multiplayer/${res.payload.id}`);
            }}
          />
        ),
      });
      openPersonInvites.add(inviteKey);
    };

    /**
     * An AI player starting a game on one of the learner's skills. Every answer
     * goes back to the server, a hidden one included: it decides when, and on
     * what, this person is invited next.
     */
    const onAiInvite = (payload: AiMultiplayerInviteNotifType) => {
      const batchId = payload?.batch_id;
      if (!batchId) return;
      const respond = (outcome: AiInviteResponse) =>
        dispatch(respondAiMultiplayerInvite({ batchId, outcome }));
      const { pathname } = window.location;
      if (
        isDoNotDisturbPath(pathname) ||
        openPersonInvites.size > 0 ||
        openAiInvite !== null
      ) {
        respond('Suppressed');
        return;
      }
      // Quiet in a background tab: software asking, not a person, so it does
      // not get to make a sound in a tab nobody is looking at.
      if (!document.hidden) playChime();

      openAiInvite = enqueueSnackbar('', {
        key: `ai-invite-${batchId}`,
        preventDuplicate: true,
        persist: true,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        onExited: () => {
          openAiInvite = null;
        },
        content: (key: SnackbarKey) => (
          <InviteToast
            id={key}
            ai={{ invitedCount: payload.invited_count ?? 1 }}
            payload={{
              skill_name: payload.skill?.name,
              skill_s3_url: payload.skill?.s3_url,
              from_user_id: payload.persona?.user_id,
              from_user_name: payload.persona?.display_name,
              from_user_picture: payload.persona?.avatar_url,
            }}
            onDismiss={() => {
              closeSnackbar(key);
              respond('Dismissed');
            }}
            onTimeout={(seen) => {
              closeSnackbar(key);
              // Never on screen is not a no.
              respond(seen ? 'TimedOut' : 'Suppressed');
            }}
            onMute={() => {
              closeSnackbar(key);
              respond('MutedToday');
              enqueueSnackbar('No more AI invites today.', {
                variant: 'success',
              });
            }}
            onOpenSettings={() => {
              closeSnackbar(key);
              respond('Dismissed');
              navigate('/settings');
            }}
            onJoin={async () => {
              closeSnackbar(key);
              const res = await dispatch(
                acceptAiMultiplayerInvite({ batchId }),
              );
              if (
                acceptAiMultiplayerInvite.fulfilled.match(res) &&
                res.payload?.room_id
              )
                navigate(`/skill-builder/multiplayer/${res.payload.room_id}`);
            }}
          />
        ),
      });
    };

    if (userId && !isOffline) {
      warmChime();
      socket.on('connect', onConnect);
      socket.on('disconnect', onDisconnect);
      socket.on('CreditChange', onCreditChange);
      socket.on('AICreditChange', onAiCreditChange);
      socket.on('XpChange', onXpChange);
      socket.on('MultiplayerRoomInvite', onRoomInvite);
      socket.on('AiMultiplayerInvite', onAiInvite);
      socket.connect();
    }

    return () => {
      socket.off('MultiplayerRoomInvite', onRoomInvite);
      socket.off('AiMultiplayerInvite', onAiInvite);
      if (socket.hasListeners('disconnect')) {
        socket.off('disconnect', onDisconnect);
      }
      if (socket.hasListeners('connect')) {
        socket.off('connect', onConnect);
      }
      socket.off('CreditChange', onCreditChange);
      socket.off('AICreditChange', onAiCreditChange);
      socket.off('XpChange', onXpChange);
      socket.disconnect();
    };
  }, [userId, isOffline, dispatch, navigate]);

  React.useEffect(() => {
    if (userToken) {
      (async () => {
        const res = await dispatch(userSelf());

        if (userSelf.fulfilled.match(res)) {
          dispatch(setIsServerNotAvailable(false));
          return;
        }

        if ([401, 403].includes((res?.payload as any)?.status ?? 0)) {
          rememberReturnPath();
          localStorage.removeItem('loginToken');
          // `/` leaves the SPA, so the signed-out sync would never get to it.
          // In Debug Mode the subscription is the admin's, so it stays.
          if (!isAdminMode()) await dropSignedOutPushSubscription();
          window.location.replace('/');
        } else if ([502, 503].includes((res?.payload as any)?.status ?? 0)) {
          dispatch(setIsServerNotAvailable(true));
        }
      })();
    }
  }, [userToken, dispatch]);

  // The signed-in user's experiment arms, fetched once up front so an upsell
  // opening later already knows which variant to show.
  const { data: myExperiments } = useGetMyExperimentsQuery(
    String(user?.id ?? ''),
    { skip: !isLoginSuccess || !user?.id },
  );
  // Pro's price is the user's offer arm, set by the server on the store items.
  // Those are fetched once a session, so re-read them when the user or their
  // arms change (a login without a reload, an experiment started or paused):
  // a tab must not show one price while checkout charges another.
  const hasShopItems = useAppSelector(
    (state) => !!state.store.shopItems.length,
  );
  const armsKey = `${user?.id ?? ''}|${JSON.stringify(
    myExperiments?.assignments ?? {},
  )}`;
  const pricedFor = React.useRef(armsKey);
  React.useEffect(() => {
    if (pricedFor.current === armsKey) return;
    pricedFor.current = armsKey;
    if (hasShopItems) dispatch(getStoreItems());
  }, [armsKey, hasShopItems, dispatch]);

  return (
    <>
      {appRoutes}
      <AddAiCreditsDialog />
      <SubscriptionCancelNotice />
      <AiAssistant />
    </>
  );
};

export const AppRouter = () => {
  const { isOffline, isServerNotAvailable } = useAppSelector(
    (state) => state.appState,
  );
  const isInWebView = !!(window as any)?.ReactNativeWebView;
  const dispatch = useAppDispatch();
  useAccountMemory();

  React.useEffect(() => {
    const handleOnline = () => dispatch(setIsOffline(false));
    const handleOffline = () => dispatch(setIsOffline(true));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [dispatch]);

  // When another tab logs out, redirect this tab immediately
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LS_KEY_LOGIN_TOKEN && e.newValue === null) {
        window.location.replace('/');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Stale-deploy chunk failure: reload through the shared cooldown so a chunk
  // that is gone for good can't reload-loop. Only swallow the error when we do
  // reload — a swallowed import resolves to undefined, so once the cooldown
  // blocks the reload the error must propagate and fail visibly.
  React.useEffect(() => {
    const onViteError = (event: VitePreloadErrorEvent) => {
      if (reloadForChunkFailure()) event.preventDefault();
    };
    window.addEventListener('vite:preloadError', onViteError);

    return () => {
      window.removeEventListener('vite:preloadError', onViteError);
    };
  }, []);

  return (
    <>
      <OfflineComponent open={isOffline} />
      {isServerNotAvailable && <MakingBetter open={isServerNotAvailable} />}
      <Router>
        {isInWebView && <AppMessageHandler />}
        <Routes />
      </Router>
    </>
  );
};

export default AppRouter;
